<template>
  <div>
    <WdsMenu z-index="10" global>
      <template #trigger>
        <WdsButtonGhost class="tw-float-right" data-test="candidate-menu-trigger">
          <WdsIcon name="overflow-menu-horizontal" />
        </WdsButtonGhost>
      </template>
      <WdsMenuOption
        v-if="canShareJob"
        :disabled="inviteToApplyDisabled"
        data-test="candidate-menu-invite-to-apply"
        @click="showSendInviteModal = true"
      >
        {{ $t('Invite to apply') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="jobObj.viewerCanCreateHire && hireOfferLink"
        :href="isSupplierJob ? undefined : hireOfferLink"
        :disabled="isSupplierJob"
        :title="isSupplierJob ? $t('The client must hire this candidate on their job first') : undefined"
        data-test="candidate-menu-create-offer"
      >
        {{ $t('Create hire offer') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="canPresentToClient"
        :disabled="presentToClientDisabled"
        :title="alreadyPresentedToClient ? $t('This candidate has already been presented to the client') : presentToClientDisabled ? $t('The candidate must submit an offer before they can be presented') : undefined"
        data-test="candidate-menu-present-to-client"
        @click="presentToClientDisabled ? undefined : (showPresentToClientModal = true)"
      >
        {{ $t('Present to client') }}
      </WdsMenuOption>
      <!-- msp-mvp flag is resolved at the parent level (CandidatesSection)
           and passed down as a prop. This keeps the option painting in the
           same render pass as the rest of the menu, instead of popping in
           after a per-menu Apollo query resolves. -->
      <template v-if="mspMvpEnabled && !supplierViewer">
        <WdsMenuOption
          data-test="candidate-menu-set-step"
          @click="initStepModal"
        >
          {{ $t('Update stage') }}
        </WdsMenuOption>
        <WdsMenuOption
          v-if="canOnboardWorker"
          :loading="onboardInProgress"
          data-test="candidate-menu-onboard-worker"
          @click="showOnboardWorkerModal = true"
        >
          {{ $t('Onboard worker') }}
        </WdsMenuOption>
      </template>
      <template v-else>
        <WdsMenuOption
          v-if="canUpdateStageAsClient"
          data-test="candidate-menu-set-step"
          @click="initStepModal"
        >
          {{ $t('Update stage') }}
        </WdsMenuOption>
      </template>
      <WdsMenuOption
        v-if="canWithdraw"
        :loading="withdrawInProgress"
        theme="DESTRUCTIVE"
        data-test="candidate-menu-withdraw"
        @click="handleWithdrawCandidate"
      >
        {{ $t('Withdraw from client') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="jobCandidate.viewerCanWithdraw"
        :loading="withdrawOwnInProgress"
        theme="DESTRUCTIVE"
        data-test="candidate-menu-withdraw-own"
        @click="showWithdrawOwnModal = true"
      >
        {{ $t('Withdraw candidate') }}
      </WdsMenuOption>
      <slot name="action" />
      <WdsMenuOption
        v-if="jobCandidate.bid?.message?.url"
        :href="jobCandidate.bid.message.url"
        data-test="candidate-menu-view-application"
      >
        {{ $t('View application') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-for="(cvFile, cvIndex) in cvFiles"
        :key="cvFile.id"
        :data-test="`candidate-menu-view-cv-${cvIndex}`"
        @click="openCv(cvFile.url)"
      >
        {{ cvFiles.length > 1 ? $t('View CV: :name', { name: cvFile.name }) : $t('View CV') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="conversationLink"
        :href="conversationLink"
        data-test="candidate-menu-open-conversation"
      >
        {{ $t('Open conversation') }}
      </WdsMenuOption>
      <WdsMenuOption
        v-if="canReject"
        theme="DESTRUCTIVE"
        data-test="candidate-menu-remove"
        @click="showRemoveCandidateModal = true"
      >
        {{ $t('Remove candidate') }}
      </WdsMenuOption>
    </WdsMenu>
    <teleport to="body">
      <WdsModal
        :show="showRemoveCandidateModal"
        custom-size="420px"
        data-test="candidate-menu-remove-candidate-modal"
        @cancel="showRemoveCandidateModal = false"
      >
        <template #header>
          <div class="gradient" />
        </template>

        <RemoveCandidateModal
          :company-id="jobObj.company.id"
          :job-id="jobObj.id"
          :job-candidate-id="jobCandidate.id"
          :job-candidate-worker-id="jobCandidate.worker.id"
          :job-candidate-worker-name="jobCandidate.worker.name"
          :job-candidate-worker-avatar="jobCandidate.worker.avatar"
          :job-candidate-worker-title="jobCandidate.worker.jobTitle ?? ''"
          :offer="jobCandidate?.bid ?? undefined"
          @cancel="showRemoveCandidateModal = false"
          @candidate-updated="handleCandidateUpdated"
        />
      </WdsModal>
      <WdsModal
        :show="showSendInviteModal"
        :title="$t('Send Invitation')"
        data-test="candidate-menu-send-invitiation-modal"
        @cancel="showSendInviteModal = false"
      >
        <SendCandidateInvitationModal
          :job-id="jobObj.id"
          :worker="jobCandidate.worker"
          @cancel="showSendInviteModal = false"
          @candidate-updated="handleCandidateUpdated"
        />
      </WdsModal>
      <WdsModal
        :show="showPresentToClientModal"
        :title="presentToClientTitle"
        data-test="candidate-menu-present-to-client-modal"
        @cancel="showPresentToClientModal = false"
      >
        <PresentToClientModal
          :job-candidate="jobCandidate"
          :client-job-name="jobObj.sourceJob?.name"
          :source-job-id="jobObj.sourceJob?.id"
          :rate-cards-enabled="rateCardsEnabled"
          @cancel="showPresentToClientModal = false"
          @candidate-presented="handleCandidateUpdated"
        />
      </WdsModal>
      <WdsModal
        :show="showOnboardWorkerModal"
        :title="$t('Start worker onboarding')"
        custom-size="420px"
        data-test="candidate-menu-onboard-worker-modal"
        @cancel="showOnboardWorkerModal = false"
      >
        <div v-if="onboardModalLoading" class="tw-flex tw-justify-center">
          <WdsSpinner indeterminate />
        </div>
        <div v-else-if="onboardModalError" class="tw-flex tw-flex-col tw-items-start tw-gap-4">
          <WdsText error>
            {{ $t('We couldn’t load the onboarding details. Please try again.') }}
          </WdsText>
          <WdsButton data-test="candidate-menu-onboard-worker-retry" @click="refetchOnboardModal()">
            {{ $t('Try again') }}
          </WdsButton>
        </div>
        <div v-else class="tw-flex tw-flex-col tw-gap-4">
          <template v-if="isManagedWorker">
            <WdsText
              v-if="isTrustedContact"
              :sub-text="$t('This worker is already in your talent pool and is managed by their supplier.')"
            />
            <template v-else>
              <WdsText
                :sub-text="$t('This will add the worker to your talent pool. The worker is managed by their supplier, so the worker will not be invited into Worksome.')"
              />
              <WdsText
                :sub-text="$t('Instead, the supplier will be asked to provide the required information and compliance documents on the worker’s behalf before they can be engaged on a hire.')"
              />
              <WdsText
                :sub-text="$t('The worker will also appear in the compliance tracker so the supplier’s progress can be reviewed.')"
              />
            </template>
            <WdsText
              v-if="requestsHireDetails"
              :sub-text="isTrustedContact
                ? $t('We’ll share the draft hire with the supplier so they can add some additional hire details.')
                : $t('We’ll also share the draft hire with the supplier so they can add some additional hire details.')"
            />
          </template>
          <template v-else>
            <WdsText
              v-if="isTrustedContact"
              :sub-text="$t('This worker is already in your talent pool.')"
            />
            <template v-else>
              <WdsText
                :sub-text="$t('This will add the worker to your talent pool and start the onboarding process in Worksome.')"
              />
              <WdsText
                :sub-text="$t('The worker will be invited to provide the required information and compliance documents needed before they can be engaged on a hire.')"
              />
              <WdsText
                :sub-text="$t('They will also appear in the compliance tracker so their progress can be reviewed.')"
              />
            </template>
            <WdsText
              v-if="requestsHireDetails"
              :sub-text="isTrustedContact
                ? $t('We’ll share the draft hire with the worker so they can add some additional hire details before it can be offered.')
                : $t('We’ll also share the draft hire with the worker so they can add some additional hire details.')"
            />
          </template>
          <WdsText
            v-if="isTrustedContact && !requestsHireDetails"
            :sub-text="$t('This will move the candidate on to onboarding.')"
          />
        </div>
        <template #footer>
          <div class="tw-flex tw-justify-end tw-w-full tw-gap-2">
            <WdsButton
              :disabled="onboardInProgress"
              data-test="candidate-menu-onboard-worker-cancel"
              @click="showOnboardWorkerModal = false"
            >
              {{ $t('Cancel') }}
            </WdsButton>
            <WdsButton
              primary
              :disabled="onboardInProgress || onboardModalLoading || !!onboardModalError"
              :loading="onboardInProgress"
              data-test="candidate-menu-onboard-worker-confirm"
              @click="handleOnboardWorker"
            >
              {{ $t('Start onboarding') }}
            </WdsButton>
          </div>
        </template>
      </WdsModal>
      <WdsModal
        :show="showWithdrawOwnModal"
        :title="$t('Withdraw candidate')"
        custom-size="420px"
        data-test="candidate-menu-withdraw-own-modal"
        @cancel="showWithdrawOwnModal = false"
      >
        <WdsText
          :sub-text="$t('This removes :name from the hiring process for this job. The client will see the candidate as withdrawn.', { name: jobCandidate.worker.name })"
        />
        <template #footer>
          <div class="tw-flex tw-justify-end tw-w-full tw-gap-2">
            <WdsButton
              :disabled="withdrawOwnInProgress"
              data-test="candidate-menu-withdraw-own-cancel"
              @click="showWithdrawOwnModal = false"
            >
              {{ $t('Cancel') }}
            </WdsButton>
            <WdsButton
              primary
              :disabled="withdrawOwnInProgress"
              :loading="withdrawOwnInProgress"
              data-test="candidate-menu-withdraw-own-confirm"
              @click="handleWithdrawOwnCandidate"
            >
              {{ $t('Withdraw') }}
            </WdsButton>
          </div>
        </template>
      </WdsModal>
      <WdsModal
        :show="showSetStepModal"
        :title="$t('Set stage')"
        custom-size="420px"
        data-test="candidate-menu-set-step-modal"
        @cancel="showSetStepModal = false"
      >
        <div class="tw-flex tw-flex-col tw-gap-4">
          <WdsText
            :sub-text="$t('Manually assign a stage to track where this candidate is in the hiring process. This does not affect automated workflows.')"
          />
          <WdsSelect
            :list="manualStepSelectOptions"
            :model-value="selectedStepOption"
            :placeholder="$t('Select a stage')"
            can-deselect
            data-test="set-step-select"
            @select-option="(option) => (selectedStepOption = option)"
          >
            <template #option="{ option }">
              <div v-if="option.isHeader" class="header-option tw-w-full" @mousedown.stop>
                <WdsText v-if="option.label" secondary>
                  {{ option.label }}
                </WdsText>
              </div>
              <span v-else>
                {{ option.label }}
              </span>
            </template>
          </WdsSelect>
        </div>
        <template #footer>
          <div class="tw-flex tw-justify-end tw-w-full">
            <WdsButton
              primary
              :disabled="!selectedStepOption || savingStep"
              :loading="savingStep"
              data-test="set-step-save"
              @click="handleSaveStep"
            >
              {{ $t('Save') }}
            </WdsButton>
          </div>
        </template>
      </WdsModal>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsModal,
  WdsSelect,
  WdsSpinner,
  WdsText,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'

import { type FragmentType, useFragment } from '@/gql'
import { JobCandidate, JobCandidateHiringStep, JobCandidateStatus, CandidateSubmissionType } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

import { JobFragment } from '@/components/Job/graphql/fragments'
import {
  InviteCandidateToOnboardMutation,
  ShareCandidatesWithPartnerMutation,
  WithdrawForwardedCandidateMutation,
  WithdrawJobCandidateMutation,
  UpdateJobCandidateStepMutation,
} from '@/components/Job/Candidates/graphql/mutations'
import { JobCandidateManualStepsByPhase, SYSTEM_STEPS } from '@/components/Job/constants'
import { OnboardWorkerModalQuery } from '@/components/Job/Candidates/graphql/queries'
import RemoveCandidateModal from '@/components/Job/Candidates/RemoveCandidateModal.vue'
import SendCandidateInvitationModal from '@/components/Job/Candidates/SendCandidateInvitationModal.vue'
import PresentToClientModal from '@/components/Job/Candidates/PresentToClientModal.vue'

const props = withDefaults(
  defineProps<{
    job: FragmentType<typeof JobFragment>
    jobCandidate: JobCandidate
    mspMvpEnabled?: boolean
    supplierViewer?: boolean
    rateCardsEnabled?: boolean
  }>(),
  { mspMvpEnabled: false, supplierViewer: false, rateCardsEnabled: false },
)

const jobObj = useFragment(JobFragment, props.job)

// The CV is an ordinary bid attachment marked with the 'CV' tag when the supplier pitches.
const cvFiles = computed(
  () =>
    props.jobCandidate.bid?.files?.filter((file) =>
      file.tags?.some((tag) => tag.name?.toLowerCase() === 'cv'),
    ) ?? [],
)

const openCv = (url: string) => window.open(url, '_blank', 'noopener')

const emit = defineEmits(['show-drawer', 'candidate-updated'])

// --- Remove candidate logic ---
const showRemoveCandidateModal = ref(false)
const showPresentToClientModal = ref(false)

// viewerCanWithdrawForwardedCandidate isn't user-aware; suppliers use the own-candidate withdraw instead.
const canWithdraw = computed(
  () => !props.supplierViewer && props.jobCandidate.viewerCanWithdrawForwardedCandidate,
)

const handleCandidateUpdated = () => {
  emit('candidate-updated')
  showRemoveCandidateModal.value = false
  showPresentToClientModal.value = false
}

// Hired / Filled with a linked hire counts as being on a hire, like Hiring.
const isOnHire = computed(
  () =>
    props.jobCandidate.step === JobCandidateHiringStep.Hiring ||
    (props.jobCandidate.step === JobCandidateHiringStep.CompletionHired &&
      !!props.jobCandidate.hire?.id),
)

const hireOfferLink = computed(() => {
  if (props.jobCandidate.status === JobCandidateStatus.Eligible && !isOnHire.value) {
    if (props.jobCandidate.bid?.id) {
      return laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect_offer, {
        freelancer: props.jobCandidate.worker?.id,
        job: jobObj.id,
        offer: props.jobCandidate.bid.id,
      })
    }

    return laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
      freelancer: props.jobCandidate.worker?.id,
      job: jobObj.id,
    })
  }
  return null
})

const conversationLink = computed(() => {
  // Conversation on the hire takes priority
  if (props.jobCandidate.hire?.conversation) {
    return props.jobCandidate.hire.conversation.url
  }

  // Conversation from the offer
  if (props.jobCandidate.bid?.conversation) {
    return props.jobCandidate.bid.conversation.url
  }

  if (props.jobCandidate.job.conversations?.data?.length) {
    // Conversation between TC and company
    const existingConversation = props.jobCandidate.job.conversations?.data.find((conversation) =>
      conversation.participants?.data?.find(
        ({ account }) => account.id === props.jobCandidate.worker?.id,
      ),
    )

    if (existingConversation) {
      return existingConversation.url
    }
  }
  return null
})

// Send invitation logic
const showSendInviteModal = ref(false)

const canShareJob = computed(
  () =>
    jobObj.available &&
    jobObj.viewerCanEdit &&
    props.jobCandidate.status === JobCandidateStatus.Eligible &&
    (!props.jobCandidate.jobShare?.id || props.jobCandidate.jobShare?.isActive === false),
)

const inviteToApplyDisabled = computed(
  () => !jobObj.published || props.jobCandidate.step !== JobCandidateHiringStep.Shortlisted,
)

const canReject = computed(
  () =>
    props.jobCandidate.viewerCanUpdateStatus &&
    props.jobCandidate.status !== JobCandidateStatus.NonEligible &&
    !isOnHire.value,
)

// --- Mirror setup: share and present ---
const isSupplierJob = computed(() => !!jobObj.sourceJob?.id)

const canPresentToClient = computed(
  () =>
    !props.supplierViewer &&
    isSupplierJob.value &&
    props.jobCandidate.status === JobCandidateStatus.Eligible &&
    ! canWithdraw.value,
)

const alreadyPresentedToClient = computed(
  () =>
    props.jobCandidate.presentedToClient ||
    props.jobCandidate.step === JobCandidateHiringStep.SelectionPresentedToClient,
)

const presentToClientDisabled = computed(
  () => !props.jobCandidate.bid?.id || alreadyPresentedToClient.value,
)

const presentToClientTitle = computed(() => {
  const clientName = jobObj.sourceJob?.company?.name
  return clientName
    ? $t('Present to :client', { client: clientName })
    : $t('Present to client')
})

const isClientViewingPresentedCandidate = computed(
  () => !!props.jobCandidate.sourceCandidate?.id,
)

const canUpdateStageAsClient = computed(
  () =>
    isClientViewingPresentedCandidate.value &&
    props.jobCandidate.status === JobCandidateStatus.Eligible,
)

const showSetStepModal = ref(false)
const sharingInProgress = ref(false)
const withdrawInProgress = ref(false)

const showOnboardWorkerModal = ref(false)
const onboardInProgress = ref(false)

const canOnboardWorker = computed(() => props.jobCandidate.viewerCanInviteToOnboard)

// A managed worker is contracted via the supplier — the supplier handles
// onboarding and compliance on the worker's behalf, so the modal copy
// frames the action around the supplier, not the worker themselves.
const isManagedWorker = computed(
  () => props.jobCandidate.bid?.workerType === CandidateSubmissionType.Managed,
)

// Only needed once the modal opens, so the candidate list doesn't pay for them on every row.
const {
  result: onboardModalResult,
  loading: onboardModalLoading,
  error: onboardModalError,
  refetch: refetchOnboardModal,
} = useQuery(
  OnboardWorkerModalQuery,
  () => ({ id: props.jobCandidate.id }),
  () => ({
    clientId: 'publicApi',
    enabled: showOnboardWorkerModal.value,
    fetchPolicy: 'network-only' as const,
  }),
)

const isTrustedContact = computed(() => onboardModalResult.value?.jobCandidate?.workerIsTrustedContact ?? false)
const requestsHireDetails = computed(
  () => onboardModalResult.value?.jobCandidate?.onboardingRequestsHireDetails ?? false,
)

const { mutate: mutateOnboardWorker } = useMutation(InviteCandidateToOnboardMutation, {
  clientId: 'publicApi',
})

const handleOnboardWorker = async () => {
  onboardInProgress.value = true
  try {
    await mutateOnboardWorker({
      input: {
        jobCandidate: props.jobCandidate.id,
      },
    })
    showOnboardWorkerModal.value = false
    emit('candidate-updated')
  } finally {
    onboardInProgress.value = false
  }
}

const { mutate: mutateShare } = useMutation(ShareCandidatesWithPartnerMutation, {
  clientId: 'publicApi',
})

const { mutate: mutateStep } = useMutation(UpdateJobCandidateStepMutation, {
  clientId: 'publicApi',
})

const { mutate: mutateWithdraw } = useMutation(WithdrawForwardedCandidateMutation, {
  clientId: 'publicApi',
})

const manualStepSelectOptions = Object.entries(JobCandidateManualStepsByPhase).flatMap(
  ([phase, steps]) => [
    { label: phase, value: `header-${phase}`, isHeader: true as const },
    ...steps.map((step) => ({
      label: step.label,
      value: step.value,
      isHeader: false as const,
    })),
  ],
)

const selectedStepOption = ref<{ label: string; value: string } | null>(null)
const savingStep = ref(false)

const initStepModal = () => {
  const currentStep = props.jobCandidate.step
  if (SYSTEM_STEPS.has(currentStep)) {
    selectedStepOption.value = null
  } else {
    selectedStepOption.value = manualStepSelectOptions.find(
      (opt) => !opt.isHeader && opt.value === currentStep,
    ) ?? null
  }
  showSetStepModal.value = true
}

const handleSaveStep = async () => {
  if (!selectedStepOption.value) return
  savingStep.value = true
  try {
    await mutateStep({
      input: {
        jobCandidate: props.jobCandidate.id,
        step: selectedStepOption.value.value as JobCandidateHiringStep,
      },
    })
    showSetStepModal.value = false
    emit('candidate-updated')
  } finally {
    savingStep.value = false
  }
}

const handleShareWithPartner = async () => {
  sharingInProgress.value = true
  try {
    await mutateShare({
      input: {
        jobCandidates: [props.jobCandidate.id],
      },
    })
    emit('candidate-updated')
  } finally {
    sharingInProgress.value = false
  }
}

const handleWithdrawCandidate = async () => {
  withdrawInProgress.value = true
  try {
    await mutateWithdraw({
      input: {
        jobCandidate: props.jobCandidate.id,
      },
    })
    emit('candidate-updated')
  } finally {
    withdrawInProgress.value = false
  }
}

const showWithdrawOwnModal = ref(false)
const withdrawOwnInProgress = ref(false)

const { mutate: mutateWithdrawOwn } = useMutation(WithdrawJobCandidateMutation, {
  clientId: 'publicApi',
})

const handleWithdrawOwnCandidate = async () => {
  withdrawOwnInProgress.value = true
  try {
    await mutateWithdrawOwn({
      input: {
        jobCandidate: props.jobCandidate.id,
      },
    })
    showWithdrawOwnModal.value = false
    emit('candidate-updated')
  } finally {
    withdrawOwnInProgress.value = false
  }
}
</script>

<style scoped lang="scss">
.gradient {
  @apply tw-bg-gradient-to-r tw-from-indigo-400 tw-to-red-300;
  @apply tw-flex-grow tw--my-6 tw--ml-6 tw-rounded-t;
  margin-right: -3.75rem;
}

:deep(.wds-select-list) {
  .wds-select-option {
    &:has(.header-option) {
      @apply tw-m-0 tw-p-0 tw-rounded-none tw-border-t tw-border-[#f0f0f0];
      &:hover {
        @apply tw-bg-white;
      }
    }

    &:first-of-type:has(.header-option) {
      @apply tw-border-t-0;

      .header-option {
        @apply tw-pt-1;
      }
    }
  }
}

.header-option {
  @apply tw-pt-3 tw-pb-2 tw-px-4 tw-cursor-auto;
}
</style>
